import React from 'react';
import { DrugAllergyItem, DrugAllergyDetail } from '../../types/api';
import { AlertModalState, ConfirmModalState } from '../../types/dispense';
import { formatDate } from '../../utils/date';

interface DrugAllergyModalProps {
  show: boolean;
  drugAllergyDetails: DrugAllergyDetail[];
  drugAllergyLoading: boolean;
  drugList: DrugAllergyItem[];
  drugSearchTerm: string;
  selectedDrug: DrugAllergyItem | null;
  editingDrugAllergy: DrugAllergyDetail | null;
  isCheckedOut: boolean;
  isPatientPicked: boolean;
  patientName: string;
  patientNric: string;
  setShow: (show: boolean) => void;
  setSelectedDrug: (drug: DrugAllergyItem | null) => void;
  setDrugSearchTerm: (term: string) => void;
  setEditingDrugAllergy: (allergy: DrugAllergyDetail | null) => void;
  setIsAddingNewDrugAllergy: (value: boolean) => void;
  resetDrugAllergyForm: () => void;
  handleDrugSearchChange: (value: string) => void;
  handleSelectDrug: (drug: DrugAllergyItem) => void;
  handleEditDrugAllergy: (allergy: DrugAllergyDetail) => void;
  handleRemoveDrugAllergy: (drugCode: string) => void;
  handleSaveNoDrugAllergy: () => Promise<void>;
}

export function DrugAllergyModal({
  show, drugAllergyDetails, drugAllergyLoading, drugList, drugSearchTerm,
  selectedDrug, editingDrugAllergy, isCheckedOut, isPatientPicked,
  patientName, patientNric,
  setShow, setSelectedDrug, setDrugSearchTerm, setEditingDrugAllergy,
  setIsAddingNewDrugAllergy, resetDrugAllergyForm,
  handleDrugSearchChange, handleSelectDrug, handleEditDrugAllergy,
  handleRemoveDrugAllergy, handleSaveNoDrugAllergy
}: DrugAllergyModalProps) {
  if (!show) return null;

  const handleClose = () => {
    setShow(false);
    setSelectedDrug(null);
    setEditingDrugAllergy(null);
    setIsAddingNewDrugAllergy(false);
    resetDrugAllergyForm();
  };

  return (
    <div
      className="modal-overlay"
      onClick={handleClose}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
    >
      <div
        className="modal modal-large"
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: '600px', maxHeight: '90vh', background: '#fff', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div className="modal-header" style={{ padding: '20px', borderBottom: '2px solid var(--border)', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div className="modal-title" style={{ fontSize: '20px', fontWeight: 700, color: '#0c4a6e', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '24px' }}>💊</span>
            Drug Allergy Management
          </div>
          <button
            className="modal-close"
            onClick={() => { setShow(false); setSelectedDrug(null); setDrugSearchTerm(''); }}
            style={{ background: 'rgba(0, 0, 0, 0.1)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', fontSize: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1e293b', fontWeight: 700, transition: 'all 0.2s ease', flexShrink: 0 }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(220, 38, 38, 0.1)'; e.currentTarget.style.color = '#dc2626'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)'; e.currentTarget.style.color = '#1e293b'; }}
          >
            &times;
          </button>
        </div>

        <div style={{ padding: '16px', maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}>
          {/* Patient Info */}
          <div style={{ marginBottom: '20px', paddingBottom: '16px', borderBottom: '2px solid var(--border)', padding: '16px', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', borderRadius: '12px', border: 'none' }}>
            <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '6px', color: '#0c4a6e' }}>{patientName}</div>
            <div style={{ fontSize: '14px', color: '#075985', fontWeight: 500 }}>{patientNric}</div>
          </div>

          {/* Current Allergies List */}
          <div style={{ marginBottom: '28px' }}>
            <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '20px' }}>💊</span>
              Current Drug Allergies
            </div>
            {drugAllergyLoading ? (
              <div style={{ padding: '32px 20px', textAlign: 'center', color: '#64748b', fontSize: '15px' }}>
                <div style={{ marginBottom: '8px' }}>⏳</div>
                Loading allergies...
              </div>
            ) : drugAllergyDetails.length === 0 ? (
              <div style={{ padding: '32px 20px', textAlign: 'center', color: '#64748b', fontStyle: 'italic', fontSize: '15px', background: '#f8fafc', borderRadius: '12px', border: '2px dashed var(--border)' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>✓</div>
                No drug allergies recorded
              </div>
            ) : (
              <div style={{ border: '2px solid var(--border)', borderRadius: '12px', maxHeight: '280px', overflowY: 'auto', background: '#fff', boxShadow: 'var(--shadow-sm)' }}>
                {drugAllergyDetails.map((allergy, index) => (
                  <div
                    key={allergy.ulid}
                    onClick={() => {
                      if (!isCheckedOut && isPatientPicked && allergy.drugCode !== 'NIL') {
                        handleEditDrugAllergy(allergy);
                      }
                    }}
                    style={{
                      padding: '16px 20px', minHeight: '64px',
                      borderBottom: index < drugAllergyDetails.length - 1 ? '1px solid var(--border)' : 'none',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      background: editingDrugAllergy?.ulid === allergy.ulid ? 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)' : '#fff',
                      transition: 'background 0.15s ease',
                      cursor: (!isCheckedOut && isPatientPicked && allergy.drugCode !== 'NIL') ? 'pointer' : 'default',
                      borderLeft: editingDrugAllergy?.ulid === allergy.ulid ? '4px solid var(--primary)' : '4px solid transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!isCheckedOut && isPatientPicked && allergy.drugCode !== 'NIL' && editingDrugAllergy?.ulid !== allergy.ulid) {
                        e.currentTarget.style.background = '#f1f5f9';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (editingDrugAllergy?.ulid !== allergy.ulid) {
                        e.currentTarget.style.background = '#fff';
                      }
                    }}
                  >
                    <div style={{ flex: 1, paddingRight: '12px' }}>
                      <div style={{ fontWeight: 600, marginBottom: '6px', fontSize: '16px', color: editingDrugAllergy?.ulid === allergy.ulid ? '#0c4a6e' : '#1e293b', lineHeight: '1.4', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {allergy.drugName || allergy.drugCode}
                        {editingDrugAllergy?.ulid === allergy.ulid && (
                          <span style={{ fontSize: '12px', color: '#0284c7', fontWeight: 600 }}>✏️ Editing</span>
                        )}
                      </div>
                      {allergy.dateCreated && (
                        <div style={{ fontSize: '13px', color: editingDrugAllergy?.ulid === allergy.ulid ? '#075985' : '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span>📅</span>
                          Added: {formatDate(allergy.dateCreated)}
                        </div>
                      )}
                    </div>
                    {!isCheckedOut && isPatientPicked && allergy.drugCode !== 'NIL' && (
                      <button
                        className="btn btn-danger"
                        onClick={(e) => { e.stopPropagation(); handleRemoveDrugAllergy(allergy.drugCode); }}
                        style={{ padding: '10px 16px', fontSize: '14px', minWidth: '80px', minHeight: '44px', fontWeight: 600, borderRadius: '8px', boxShadow: 'var(--shadow-sm)', flexShrink: 0 }}
                        title="Remove this drug allergy"
                      >
                        ✕ Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* No Known Drug Allergy Button */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
            <button
              className="btn btn-secondary"
              onClick={handleSaveNoDrugAllergy}
              style={{ width: '100%', padding: '16px', fontSize: '16px', fontWeight: 700, minHeight: '56px', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', transition: 'all 0.2s ease', border: '2px solid var(--border)' }}
            >
              ✓ No Known Drug Allergy
            </button>
          </div>

          {/* Add New Allergy - Drug Search */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '20px' }}>➕</span>
              Add Drug Allergy
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontSize: '15px', fontWeight: 600, color: '#1e293b' }}>🔍 Search Drug</label>
              <input
                type="text"
                placeholder="Type drug name or code to search..."
                value={drugSearchTerm}
                onChange={(e) => handleDrugSearchChange(e.target.value)}
                autoFocus
                style={{ width: '100%', padding: '16px', border: '2px solid var(--border)', borderRadius: '12px', fontSize: '16px', minHeight: '52px', background: '#fff', transition: 'all 0.2s ease', boxShadow: 'var(--shadow-sm)' }}
                onFocus={(e) => { e.target.style.borderColor = 'var(--primary)'; e.target.style.boxShadow = '0 0 0 3px rgba(2, 132, 199, 0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'var(--shadow-sm)'; }}
              />
              {drugSearchTerm && (
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>💡</span>
                  {drugList.length > 0 ? `Found ${drugList.length} drug${drugList.length !== 1 ? 's' : ''}` : 'No results found'}
                </div>
              )}
            </div>

            {/* Drug Selection List */}
            {drugList.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <label style={{ fontSize: '15px', fontWeight: 600, color: '#1e293b' }}>Select Drug</label>
                  <span style={{ fontSize: '13px', color: '#64748b', background: '#f1f5f9', padding: '4px 10px', borderRadius: '12px', fontWeight: 500 }}>{drugList.length} found</span>
                </div>
                <div style={{ border: '2px solid var(--border)', borderRadius: '12px', maxHeight: '320px', overflowY: 'auto', background: '#fff', boxShadow: 'var(--shadow-sm)', WebkitOverflowScrolling: 'touch' }}>
                  {drugList.map((drug, index) => (
                    <div
                      key={drug.code}
                      onClick={() => handleSelectDrug(drug)}
                      style={{
                        padding: '18px 20px', minHeight: '72px', cursor: 'pointer',
                        borderBottom: index < drugList.length - 1 ? '1px solid var(--border)' : 'none',
                        background: selectedDrug?.code === drug.code ? 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)' : '#fff',
                        transition: 'all 0.15s ease',
                        borderLeft: selectedDrug?.code === drug.code ? '4px solid var(--primary)' : '4px solid transparent',
                        display: 'flex', flexDirection: 'column', justifyContent: 'center'
                      }}
                      onMouseLeave={(e) => { if (selectedDrug?.code !== drug.code) e.currentTarget.style.background = '#fff'; }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: '6px', fontSize: '16px', color: selectedDrug?.code === drug.code ? '#0c4a6e' : '#1e293b', lineHeight: '1.4' }}>
                        {drug.description}
                      </div>
                      <div style={{ fontSize: '13px', color: selectedDrug?.code === drug.code ? '#075985' : '#64748b', fontWeight: selectedDrug?.code === drug.code ? 500 : 400 }}>
                        Code: {drug.code}
                      </div>
                      {selectedDrug?.code === drug.code && (
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#0284c7', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          ✓ Selected
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="modal-actions">
          <button
            className="btn btn-secondary"
            onClick={handleClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
